/**
 * FamilyPage - Familjeläge
 * 
 * Premiumfunktion som låter användare dela sin beredskap med upp till 5 familjemedlemmar.
 * 
 * VIKTIGT: Endast personen som "äger" familjen (den som bjuder in) behöver Premium.
 * Inbjudna medlemmar kan ha gratiskonton.
 */

import { useState, useEffect } from 'react';
import { useToastStore } from '@/stores/toast-store';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { useFamilyStore } from '@/stores/family-store';
import ModalBackdrop from '@/components/ModalBackdrop';
import NativePremiumNoticeModal from '@/components/NativePremiumNoticeModal';
import {
  Users,
  Crown,
  UserPlus,
  Mail,
  X,
  Check,
  Clock,
  AlertCircle,
  Trash2,
  LogOut,
  Sparkles,
  Heart,
  Shield,
  Info,
  Send,
  CheckCircle2,
  Pencil,
} from 'lucide-react';
import { getNativePlatform } from '@/utils/native-platform';

export default function FamilyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    family,
    isOwner,
    pendingInvitations,
    sentInvitations,
    createFamily,
    inviteMember,
    removeMember,
    cancelInvitation,
    leaveFamily,
    acceptInvitation,
    declineInvitation,
    fetchFamily,
    setFamily,
    canInviteMore,
  } = useFamilyStore();

  const toast = useToastStore();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showNativePremiumNotice, setShowNativePremiumNotice] = useState(false);
  const [confirmRemoveMember, setConfirmRemoveMember] = useState<{ userId: string; name: string } | null>(null);
  const [familyName, setFamilyName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  

  const isPremium = user?.subscriptionTier === 'premium';
  const nativePlatform = getNativePlatform();

  // Hantera skapande av familj
  const handleCreateFamily = async () => {
    if (!user || !familyName.trim()) return;
    const res = await createFamily(familyName.trim(), user.id, user.displayName, user.email);
    if (res) {
      setFamilyName('');
      setShowCreateModal(false);
      toast.push({ title: 'Familj skapad', description: `Du skapade ${res.name}`, variant: 'success' });
    } else {
      toast.push({ title: 'Misslyckades', description: 'Kunde inte skapa familj', variant: 'error' });
    }
  };

  // Hantera inbjudan
  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    // Validera e-post format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail.trim())) {
      toast.push({ title: 'Misslyckades', description: 'Ogiltig e-postadress. Måste innehålla @ och minst en .', variant: 'error' });
      return;
    }
    // Prevent inviting own email
    if (user && inviteEmail.trim().toLowerCase() === user.email.toLowerCase()) {
      toast.push({ title: 'Misslyckades', description: 'Du kan inte bjuda in din egen e-postadress', variant: 'error' });
      return;
    }
    // Client-side guard: show friendly message if no slots
    if (!canInviteMore()) {
      toast.push({ title: 'Misslyckades', description: 'Inga lediga platser för fler inbjudningar', variant: 'error' });
      return;
    }
    // Prevent duplicate invite (client-side quick check)
    const existing = sentInvitations.find(inv => (inv.inviteeEmail || '').toLowerCase() === inviteEmail.trim().toLowerCase() && inv.status === 'pending');
    if (existing) {
      toast.push({ title: 'Misslyckades', description: 'Det finns redan en väntande inbjudan till denna e-post', variant: 'error' });
      return;
    }
    const invitation = await inviteMember(inviteEmail.trim(), inviteMessage.trim() || undefined);
    if (invitation) {
      setInviteEmail('');
      setInviteMessage('');
      setShowInviteModal(false);
      if (family?.id) await fetchFamily(family.id);
      toast.push({ title: 'Inbjudan skickad', description: `Skickat till ${invitation.inviteeEmail}`, variant: 'success' });
    } else {
      toast.push({ title: 'Misslyckades', description: 'Kunde inte skicka inbjudan', variant: 'error' });
    }
  };

  

  // Hantera accepterad inbjudan
  const handleAcceptInvitation = async (invitationId: string) => {
    if (!user) return;
    const res = await acceptInvitation(invitationId, user.id, user.displayName, user.email);
    if (res && (res as unknown as { id?: string }).id) {
      // ensure store is up to date
      await fetchFamily((res as unknown as { id: string }).id);
      toast.push({ title: 'Välkommen till familjen', description: `Du är nu medlem`, variant: 'success' });
    }
  };

  // Fetch family when user has familyId
  useEffect(() => {
    if (user?.familyId) {
      fetchFamily(user.familyId).catch(() => {});
    } else {
      // clear local family state when user leaves family
      setFamily(null);
    }
  // Note: fetchFamily and setFamily are stable from the store, but include them if lint requires.
   
  }, [user?.familyId]);

  // Om användaren inte är Premium och inte är med i en familj
  if (!isPremium && !family) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold md:text-3xl flex items-center"><Heart className="h-6 w-6 text-primary mr-3 shrink-0" />{t('family.title')}</h1>
          <p className="mt-2 text-muted-foreground">{t('family.subtitle')}</p>
        </div>

        {/* Pending invitations för gratisanvändare */}
        {pendingInvitations.length > 0 && (
          <div className="mb-8 space-y-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {t('family.pendingInvitations')}
            </h2>
            {pendingInvitations.map((invitation) => (
              <div
                key={invitation.id}
                className="rounded-lg border bg-card p-4"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">{invitation.familyName}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('family.invitedBy', { name: invitation.invitedBy })}
                    </p>
                    {invitation.message && (
                      <p className="mt-2 text-sm italic">"{invitation.message}"</p>
                    )}
                  </div>
                </div>

                {/* Premium info för inbjudan */}
                <div className="mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                  <div className="flex gap-2">
                    <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800 dark:text-amber-200">
                      <p className="font-medium">{t('family.noPremiumNeeded')}</p>
                      <p className="mt-1 text-amber-700 dark:text-amber-300">
                        {t('family.noPremiumNeededDesc')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => handleAcceptInvitation(invitation.id)}
                    className="flex-1 flex items-center justify-center gap-2 btn-primary-custom"
                  >
                    <Check className="h-4 w-4" />
                    {t('family.acceptInvitation')}
                  </button>
                  <button
                    onClick={() => declineInvitation(invitation.id)}
                    className="flex items-center justify-center gap-2 rounded-lg border px-4 py-2 hover:bg-muted"
                  >
                    <X className="h-4 w-4" />
                    {t('family.declineInvitation')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Premium upsell */}
        <div className="rounded-xl border-2 border-dashed border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10 p-8">
          <div className="flex flex-col items-center text-center">
            <div className="mb-4 rounded-full bg-primary/10 p-4">
              <Crown className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-xl font-bold">{t('family.premiumRequired')}</h2>
            <p className="mt-2 text-muted-foreground max-w-md">
              {t('family.premiumRequiredDesc')}
            </p>

            {/* Fördelar */}
            <div className="mt-6 grid gap-3 w-full max-w-sm">
              {[
                { icon: Users, text: t('family.benefit.shareWithFamily') },
                { icon: Heart, text: t('family.benefit.syncData') },
                { icon: Shield, text: t('family.benefit.collectivePrep') },
              ].map((benefit, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg bg-background/50 p-3">
                  <benefit.icon className="h-5 w-5 text-primary" />
                  <span className="text-sm">{benefit.text}</span>
                </div>
              ))}
            </div>

            <button 
              onClick={() => {
                navigate('/app/settings#get-premium');
              }}
              className="mt-6 inline-flex items-center gap-2 btn-primary-custom"
            >
              <Sparkles className="h-5 w-5" />
              {t('settings.premium.enable', 'Skaffa Premium')}
            </button>

            <p className="mt-4 text-xs text-muted-foreground">
              {t('family.onlyOwnerNeedsPremium')}
            </p>
          </div>
        </div>

        <NativePremiumNoticeModal
          open={showNativePremiumNotice}
          onClose={() => setShowNativePremiumNotice(false)}
          platform={nativePlatform}
          feature={t('family.premiumRequired')}
        />
      </div>
    );
  }

  // Om användaren har en familj
  if (family) {
    const pendingInviteCount = sentInvitations.filter(inv => inv.status === 'pending').length;

    return (
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold md:text-3xl inline-flex items-center gap-2">{family.name}</h1>
              <p className="mt-2 text-muted-foreground">
                {t('family.memberCount', { count: family.members.length, max: family.maxMembers })}
              </p>
            </div>
                {isOwner && (
              <div className="ml-4">
                <button
                  onClick={() => { setFamilyName(family.name); setShowRenameModal(true); }}
                  className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
                  title={t('family.rename')}
                >
                  <span className="sr-only">{t('family.rename')}</span>
                      <Pencil className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Info om Premium för ägare */}
        {isOwner && (
          <div className="mb-6 p-4 rounded-lg bg-primary/5 border border-primary/20">
            <div className="flex gap-3">
              <Crown className="h-5 w-5 text-primary shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-primary">{t('family.youAreOwner')}</p>
                <p className="mt-1 text-muted-foreground">
                  {t('family.ownerPremiumInfo')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Info för inbjudna medlemmar */}
        {!isOwner && (
          <div className="mb-6 p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
            <div className="flex gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-green-800 dark:text-green-200">
                  {t('family.memberBenefit')}
                </p>
                <p className="mt-1 text-green-700 dark:text-green-300">
                  {t('family.memberBenefitDesc', { owner: family.ownerName })}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Medlemslista */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">{t('family.members')}</h2>
          
          {family.members.map((member) => (
            <div
              key={member.id ?? member.userId}
              className="flex items-center justify-between rounded-lg border bg-card p-4"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 14a4 4 0 10-8 0m8 0v2a2 2 0 01-2 2H8a2 2 0 01-2-2v-2a4 4 0 018 0" />
                  </svg>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{(((member as unknown) as Record<string, unknown>).givenName as string) || member.displayName || (member.email || '').split('@')[0]}</span>
                    {member.role === 'owner' && (
                      <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                        <Crown className="h-3 w-3" />
                        {t('family.ownerBadge')}
                      </span>
                    )}
                    {member.userId === user?.id && (
                      <span className="text-xs text-muted-foreground">
                        ({t('common.you')})
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{member.email}</p>
                </div>
              </div>

              {/* Ta bort medlem (bara för ägare, kan inte ta bort sig själv) */}
              {isOwner && member.role !== 'owner' && (
                <button
                  onClick={() => {
                    console.log('Remove member clicked', { userId: member.userId, name: member.displayName || member.email });
                    setConfirmRemoveMember({ userId: member.userId, name: member.displayName || member.email });
                  }}
                  className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-destructive"
                  title={t('family.removeMember')}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          {/* Invite button moved here, below members list */}
          {isOwner && canInviteMore() && (
            <div className="mt-4">
              <button
                onClick={() => {
                  setInviteMessage(t('family.defaultInviteMessage'))
                  setShowInviteModal(true)
                }}
                className="flex items-center gap-2 btn-primary-custom px-4 py-2"
              >
                <UserPlus className="h-4 w-4" />
                {t('family.inviteMember')}
              </button>
            </div>
          )}
        </div>

        {/* Väntande inbjudningar */}
        {isOwner && pendingInviteCount > 0 && (
          <div className="mt-8 space-y-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Clock className="h-5 w-5" />
              {t('family.pendingSentInvitations')}
              <span className="rounded-full bg-amber-100 dark:bg-amber-900 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-300">
                {pendingInviteCount}
              </span>
            </h2>
            
            {sentInvitations.filter(inv => inv.status === 'pending').map((invitation) => (
              <div
                key={invitation.id}
                className="flex items-center justify-between gap-3 rounded-lg border bg-card p-4"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted shrink-0">
                    <Mail className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium soft-wrap">{invitation.inviteeEmail}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('family.sentAt', { 
                        date: new Date(invitation.createdAt).toLocaleDateString('sv-SE') 
                      })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => cancelInvitation(invitation.id)}
                    className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-destructive"
                    title={t('family.cancelInvitation')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Lämna/radera familj */}
        <div className="mt-8 pt-6 border-t">
          <button
            onClick={() => setShowLeaveModal(true)}
            className="flex items-center gap-2 text-destructive hover:text-destructive/80"
          >
            <LogOut className="h-4 w-4" />
            {isOwner ? t('family.deleteFamily') : t('family.leaveFamily')}
          </button>
        </div>

        {/* Rename Family Modal */}
        {showRenameModal && (
          <div className="fixed bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
            <div className="bg-card rounded-2xl max-w-md w-full shadow-xl border border-border">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-foreground">{t('family.renameTitle', 'Byt namn på familjen')}</h2>
                  <button onClick={() => setShowRenameModal(false)} className="text-muted-foreground hover:text-foreground">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <label className="block text-sm font-medium text-foreground">{t('family.name')}</label>
                  <input
                    value={familyName}
                    onChange={(e) => setFamilyName(e.target.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg bg-input text-foreground"
                    placeholder={t('family.namePlaceholder', 'Familjens namn')}
                  />

                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => setShowRenameModal(false)}
                      className="flex-1 px-4 py-2 bg-muted text-muted-foreground rounded-xl"
                    >
                      {t('common.cancel', 'Avbryt')}
                    </button>
                    <button
                      onClick={async () => {
                        const { renameFamily } = useFamilyStore.getState();
                        const ok = renameFamily ? await renameFamily(familyName.trim()) : false;
                        setShowRenameModal(false);
                        if (ok) {
                          // refresh family from server for canonical state
                          if (family?.id) await fetchFamily(family.id);
                          toast.push({ title: t('family.renameSuccess', 'Namn uppdaterat'), variant: 'success' });
                        } else {
                          toast.push({ title: t('family.renameFailed', 'Kunde inte uppdatera namn'), variant: 'error' });
                        }
                      }}
                      className="flex-1 px-4 py-2 btn-primary-custom rounded-xl"
                    >
                      {t('family.save', 'Spara')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Invite Modal */}
        {showInviteModal && (
          <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
            <div className="w-full max-w-md rounded-xl bg-background p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">{t('family.inviteMember')}</h3>
                <button
                  onClick={() => {
                    setShowInviteModal(false)
                    setInviteMessage('')
                  }}
                  className="rounded-lg p-1 hover:bg-muted"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Premium-info i inbjudningsmodal */}
              <div className="mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                <div className="flex gap-2">
                  <Info className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-green-800 dark:text-green-200">
                    {t('family.inviteePremiumInfo')}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('family.inviteeEmail')}
                    <span className="ml-2 text-xs text-muted-foreground">{inviteEmail.length}/100</span>
                  </label>
                  <input
                    type="email"
                    maxLength={100}
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder={t('family.inviteeEmailPlaceholder')}
                    className="w-full rounded-lg border bg-background px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('family.inviteMessage')} <span className="text-muted-foreground">({t('common.optional')})</span>
                    <span className="ml-2 text-xs text-muted-foreground">{inviteMessage.length}/200</span>
                  </label>
                  <textarea
                    maxLength={200}
                    value={inviteMessage}
                    onChange={(e) => setInviteMessage(e.target.value)}
                    placeholder={t('family.inviteMessagePlaceholder')}
                    rows={3}
                    className="w-full rounded-lg border bg-background px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                  />
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => {
                    setShowInviteModal(false)
                    setInviteMessage('')
                  }}
                  className="flex-1 rounded-lg border px-4 py-2 hover:bg-muted"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={async () => {
                    await handleInvite()
                    setInviteMessage('')
                  }}
                  disabled={!inviteEmail.trim()}
                  className="flex-1 flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-white hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: '#67724e' }}
                >
                  <Send className="h-4 w-4" />
                  {t('family.sendInvitation')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Leave/Delete Modal */}
        {showLeaveModal && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="w-full max-w-md rounded-xl bg-background p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="rounded-full bg-destructive/10 p-2">
                  <AlertCircle className="h-6 w-6 text-destructive" />
                </div>
                <h3 className="text-lg font-semibold">
                  {isOwner ? t('family.deleteFamilyTitle') : t('family.leaveFamilyTitle')}
                </h3>
              </div>

              <p className="text-muted-foreground mb-6">
                {isOwner ? t('family.deleteFamilyDesc') : t('family.leaveFamilyDesc')}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowLeaveModal(false)}
                  className="flex-1 rounded-lg border px-4 py-2 hover:bg-muted"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => {
                    leaveFamily();
                    setShowLeaveModal(false);
                  }}
                  className="flex-1 rounded-lg bg-destructive px-4 py-2 text-destructive-foreground hover:bg-destructive/90"
                >
                  {isOwner ? t('family.confirmDelete') : t('family.confirmLeave')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Remove Member Modal */}
        {confirmRemoveMember && (
          <ModalBackdrop>
            <div className="w-full max-w-sm rounded-xl bg-card border border-border p-6 my-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-red-500/20">
                  <Trash2 className="h-6 w-6 text-red-400" />
                </div>
                <h2 className="text-xl font-semibold">Ta bort medlem</h2>
              </div>
              <p className="text-muted-foreground mb-2">
                Är du säker på att du vill ta bort <span className="font-medium text-foreground">{confirmRemoveMember.name}</span> från familjen?
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Medlemmen kommer inte längre ha tillgång till familjens delade beredskap.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmRemoveMember(null)}
                  className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Avbryt
                </button>
                <button
                  onClick={async () => {
                    await removeMember(confirmRemoveMember.userId);
                    setConfirmRemoveMember(null);
                  }}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  Ta bort
                </button>
              </div>
            </div>
          </ModalBackdrop>
        )}
      </div>
    );
  }

  // Premium-användare utan familj - kan skapa
  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold md:text-3xl">{t('family.title')}</h1>
        <p className="mt-2 text-muted-foreground">{t('family.subtitle')}</p>
      </div>

      {/* Pending invitations */}
      {pendingInvitations.length > 0 && (
        <div className="mb-8 space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Mail className="h-5 w-5" />
            {t('family.pendingInvitations')}
          </h2>
          {pendingInvitations.map((invitation) => (
            <div
              key={invitation.id}
              className="rounded-lg border bg-card p-4"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium">{invitation.familyName}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('family.invitedBy', { name: invitation.invitedBy })}
                  </p>
                  {invitation.message && (
                    <p className="mt-2 text-sm italic">"{invitation.message}"</p>
                  )}
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => handleAcceptInvitation(invitation.id)}
                  className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
                >
                  <Check className="h-4 w-4" />
                  {t('family.acceptInvitation')}
                </button>
                <button
                  onClick={() => declineInvitation(invitation.id)}
                  className="flex items-center justify-center gap-2 rounded-lg border px-4 py-2 hover:bg-muted"
                >
                  <X className="h-4 w-4" />
                  {t('family.declineInvitation')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state - create family */}
      <div className="rounded-xl border-2 border-dashed p-8">
        <div className="flex flex-col items-center text-center">
          <div className="mb-4 rounded-full bg-primary/10 p-4">
            <Users className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-xl font-bold">{t('family.noFamily')}</h2>
          <p className="mt-2 text-muted-foreground max-w-md">
            {t('family.noFamilyDesc')}
          </p>

          {/* Info om att endast ägaren behöver Premium */}
          <div className="mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 max-w-md">
            <div className="flex gap-2">
              <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 dark:text-amber-200 text-left">
                {t('family.onlyOwnerNeedsPremiumDetailed')}
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-6 flex items-center gap-2 rounded-lg px-6 py-3 font-medium text-white transition-colors"
            style={{ backgroundColor: '#67724e' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a6343'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#67724e'}
          >
            <UserPlus className="h-5 w-5" />
            {t('family.createFamily')}
          </button>
        </div>
      </div>

      {/* Create Family Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-xl bg-background p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('family.createFamilyTitle')}</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="rounded-lg p-1 hover:bg-muted"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('family.familyName')}
                </label>
                <input
                  type="text"
                  value={familyName}
                  onChange={(e) => setFamilyName(e.target.value)}
                  placeholder={t('family.familyNamePlaceholder')}
                  className="w-full rounded-lg border bg-background px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Info om Premium */}
              <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                <div className="flex gap-2">
                  <Crown className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-primary">{t('family.premiumOwnerInfo')}</p>
                    <p className="mt-1 text-muted-foreground">
                      {t('family.premiumOwnerInfoDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 rounded-lg border px-4 py-2 hover:bg-muted"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateFamily}
                disabled={!familyName.trim()}
                className="flex-1 rounded-lg px-4 py-2 text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: familyName.trim() ? '#67724e' : undefined }}
                onMouseEnter={(e) => { if (familyName.trim()) e.currentTarget.style.backgroundColor = '#5a6343'; }}
                onMouseLeave={(e) => { if (familyName.trim()) e.currentTarget.style.backgroundColor = '#67724e'; }}
              >
                {t('family.createFamily')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}